import React, { FC, useState, useEffect, useContext, useCallback } from "react";
import Input from "../../components/ui/input";
import Password from "src/components/ui/password";
import Button from "src/components/ui/button";
import { ResetIcon } from "../../components/icons/reset-icon";
import ReCAPTCHA from "react-google-recaptcha";
import routes from "src/config/routes";
import { useNavigate, Navigate } from "react-router-dom";
import { fadeInBottom } from "src/lib/framer-motion/fade-in-bottom";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "react-query";
import client from "../../api";
import useAuth from "../../hooks/use-auth";
import { getErrorMessage, logger, getFirstPermissionRoute } from "../../lib/utils";
import { ConfigValue } from "src/config";
import GlobalContext from "src/context/global-context";
import { LogoIcon } from "src/components/icons/logo-icon";
import InputCode from "src/components/ui/input-code";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  permissions,
} from "src/lib/constants";

const loginSchema = yup
  .object({
    email: yup
      .string()
      .required("This field is required.")
      .matches(
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        "Please write a valid email address."
      ),
    password: yup
      .string()
      .required("This field is required.")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
        "Pasword must be at least 8 characters long, include 1 uppercase letter, 1 special character and 1 number."
      ),
    totp: yup
      .string()
      .required("This field is required.")
      .matches(/^[0-9]{6}$/, "Invalid code. Please try again."),
    captcha: yup.string().required("This field is required.").min(3),
  })
  .required();
type LoginFormData = yup.InferType<typeof loginSchema>;

const forgotPasswordSchema = yup
  .object({
    email: yup
      .string()
      .required("This field is required.")
      .matches(
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        "Please write a valid email address."
      ),
  })
  .required();
type ForgotPasswordFormData = yup.InferType<typeof forgotPasswordSchema>;

const resetPasswordSchema = yup
  .object({
    otp: yup
      .string()
      .required("This field is required.")
      .matches(/^[0-9]{6}$/, "OTP must be a 6-digit code."),
    password: yup
      .string()
      .required("This field is required.")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
        "Password must be at least 8 characters long, include 1 uppercase letter, 1 special character and 1 number."
      ),
    confirm_password: yup
      .string()
      .required("This field is required.")
      .oneOf([yup.ref("password")], "Passwords do not match"),
  })
  .required();
type ResetPasswordFormData = yup.InferType<typeof resetPasswordSchema>;

type AuthView = "login" | "forgot" | "reset";

const SignInPage: FC = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<AuthView>("login");
  const [resetEmail, setResetEmail] = useState("");

  const recaptchaRef = React.createRef<ReCAPTCHA>();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { isValid, errors },
  } = useForm<LoginFormData>({
    mode: "all",
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      totp: "",
      captcha: "",
    },
  });
  const [curPassword, curEmail, totp] = watch(["password", "email", "totp"]);

  const {
    register: registerForgot,
    handleSubmit: handleSubmitForgot,
    setValue: setValueForgot,
    formState: { errors: forgotErrors, isValid: isForgotValid },
    reset: resetForgotForm,
  } = useForm<ForgotPasswordFormData>({
    mode: "all",
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: resetErrors, isValid: isResetValid },
    reset: resetResetForm,
  } = useForm<ResetPasswordFormData>({
    mode: "all",
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: {
      otp: "",
      password: "",
      confirm_password: "",
    },
  });

  const { setAlertText, setIsAlertOpened } = useContext(GlobalContext);
  const [captchaValue, setCaptchaValue] = useState("");

  const { authorize, setCurrentUser, checkPermission } = useAuth();
  const [resetpCatpcha, setResetpCatpcha] = useState(false);

  const handleReCaptchaVerify = useCallback(async () => {
    if (!recaptchaRef.current) {
      // console.error("reCAPTCHA ref не ініціалізовано");
      return;
    }

    const token = await recaptchaRef.current?.execute();
    if (String(token)) {
      setCaptchaValue(String(token));
      return token;
    } else {
      console.error("Не вдалося отримати токен від reCAPTCHA");
    }
  }, [recaptchaRef]);

  const handleGenerate = async () => {
    await handleReCaptchaVerify();
  };

  useEffect(() => {
    if (curPassword && curEmail && totp) {
      if (!captchaValue) {
        handleGenerate();
        return;
      }
    }
  }, [curPassword, curEmail, totp]);

  const { mutate: login, isLoading } = useMutation(client.auth.login, {
    onSuccess: (data) => {
      authorize(data.token);
      setCurrentUser(data.user_data, curPassword);
      const targetRoute = getFirstPermissionRoute(data.user_data);
      navigate(targetRoute);
    },
    onError: (error: any) => {
      if (error.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else {
        if (error.response) {
          const resData = error.response.data;
          const errors = resData?.errors || {};

          if (
            errors?.captcha &&
            errors.captcha[0] === "validationrecaptcha"
          ) {
            setResetpCatpcha(true);
          }

          setAlertText(getErrorMessage(error.response.data));
        } else {
          setAlertText(APISomethingWrongMsg);
        }
      }
      setIsAlertOpened(true);
    },
  });

  const { mutate: forgotPasswordMutate, isLoading: isForgotLoading } =
    useMutation(client.auth.forgotPassword, {
      onSuccess: (data: any, variables) => {
        const message =
          data?.message || "A password reset code has been sent to your email.";
        setAlertText(message);
        setIsAlertOpened(true);
        setResetEmail(variables.email);
        resetResetForm({
          otp: "",
          password: "",
          confirm_password: "",
        });
        setCurrentView("reset");
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error.response?.data) {
          setAlertText(getErrorMessage(error.response.data));
        } else if (error.message) {
          setAlertText(error.message);
        } else {
          setAlertText(APISomethingWrongMsg);
        }
        setIsAlertOpened(true);
      },
    });

  const { mutate: resetPasswordMutate, isLoading: isResetLoading } =
    useMutation(client.auth.resetPassword, {
      onSuccess: (data: any) => {
        const message =
          data?.message || "Your password has been reset successfully.";
        setAlertText(message);
        setIsAlertOpened(true);
        setValue("email", resetEmail, { shouldValidate: true });
        setValue("password", "");
        setCurrentView("login");
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else if (error.response?.data) {
          setAlertText(getErrorMessage(error.response.data));
        } else if (error.message) {
          setAlertText(error.message);
        } else {
          setAlertText(APISomethingWrongMsg);
        }
        setIsAlertOpened(true);
      },
    });

  const { isAuthorized, getCurrrentUser } = useAuth();
  if (isAuthorized) {
    const targetRoute = getFirstPermissionRoute(getCurrrentUser());
    return <Navigate to={targetRoute} />;
  }

  const onLoginSubmit = (data: LoginFormData) => {
    login({
      email: data.email,
      password: data.password,
      totp: data.totp,
      captcha: captchaValue,
    });
  };

    const onForgotSubmit = (data: ForgotPasswordFormData) => {
    forgotPasswordMutate({
      email: data.email,
    });
  };

  const onResetSubmit = (data: ResetPasswordFormData) => {
    resetPasswordMutate({
      email: resetEmail,
      otp: data.otp,
      password: data.password,
      confirm_password: data.confirm_password,
    });
  };

  const handleChange = (value: string | null) => {
    setCaptchaValue(value ? value : "");
    setValue("captcha", value ? value : "", { shouldValidate: true });

    //this.setState({ value });
    // if value is null recaptcha expired
    //if (value === null) this.setState({ expired: "true" });
  };

  const asyncScriptOnLoad = () => {
    logger.log("scriptLoad - reCaptcha Ref-", recaptchaRef);
  };

  const resetCaptcha = () => {
    recaptchaRef.current?.reset();
  };

  return (
    <motion.div
      variants={fadeInBottom()}
      className="justify-center items-center w-full m-auto"
    >
      <div className="flex items-center w-full px-4 justify-center py-[10px]">
        {currentView === "login" && (
        <form
          className="flex h-full w-full sm:w-[481px] py-8 flex-col px-4 sm:px-8 bg-white rounded-[12px]"
          onSubmit={handleSubmit(onLoginSubmit)}
        >
          <div className="flex flex-col gap-2 items-center justify-center">
            <LogoIcon className="w-[50px] h-[50px]" />
            <p className="text-[34px] font-medium font-manrope text-primary text-center sm:text-left">
              Login Backoffice
            </p>
          </div>
          <div className="flex flex-col mt-10 gap-4">
            <Input
              label="Email"
              id="new-email"
              autoComplete="new-email"
              className="w-full"
              inputClassName="h-[50px] sm:!h-[60px]"
              {...register("email")}
              error={errors.email && (errors.email.message as string)}
            />
            <Password
              label="Password"
              id="new-password"
              autoComplete="new-password"
              className="w-full"
              inputClassName="h-[50px] sm:!h-[60px]"
              {...register("password")}
              error={errors.password && (errors.password.message as string)}
            />
            <Controller
              name="totp"
              control={control}
              render={({ field }) => (
                <InputCode
                  maxLength={6}
                  label="Two-factor authentication"
                  placeholder="Enter 2FA code"
                    className="w-full"
                  inputClassName="h-[50px] sm:!h-[60px]"
                  error={errors.totp && (errors.totp.message as string)}
                  {...field}
                />
              )}
            />
            <div className="flex flex-col mt-4">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={ConfigValue.CAPTCHA!}
                className="inline-block"
                size="invisible"
                onChange={handleChange}
                asyncScriptOnLoad={asyncScriptOnLoad}
              />
            </div>
          </div>

            <div className="flex flex-col gap-4 mt-6">
            <Button
              variant="validate"
              type="submit"
              className="rounded-[10px] font-manrope text-[16px] font-bold leading-[24px] h-[64px]"
              disabled={!isValid || isLoading}
              isLoading={isLoading}
            >
              Login
            </Button>

              <div className="flex items-center justify-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    if (curEmail) {
                      setValueForgot("email", curEmail, {
                        shouldValidate: true,
                      });
                    }
                    setCurrentView("forgot");
                  }}
                  className="font-manrope text-[14px] text-primary hover:text-active font-semibold transition-colors cursor-pointer"
                >
                  Forgot Password?
                </button>
              </div>
            </div>
          </form>
        )}

        {currentView === "forgot" && (
          <form
            className="flex h-full w-full sm:w-[481px] py-8 flex-col px-4 sm:px-8 bg-white rounded-[12px]"
            onSubmit={handleSubmitForgot(onForgotSubmit)}
          >
            <div className="flex flex-col gap-2 items-center justify-center">
              <LogoIcon className="w-[50px] h-[50px]" />
              <p className="text-[30px] sm:text-[34px] font-medium font-manrope text-primary text-center">
                Forgot Password
              </p>
              <p className="text-[14px] font-manrope text-secondary text-center max-w-[340px]">
                Enter your email address to receive an OTP code to reset your
                password.
              </p>
            </div>

            <div className="flex flex-col mt-8 gap-4">
              <Input
                label="Email"
                id="forgot-email"
                placeholder="Enter your email"
                autoComplete="email"
                className="w-full"
                inputClassName="h-[50px] sm:!h-[60px]"
                {...registerForgot("email")}
                error={
                  forgotErrors.email && (forgotErrors.email.message as string)
                }
              />
            </div>

            <div className="flex flex-col gap-4 mt-8">
              <Button
                variant="validate"
                type="submit"
                className="rounded-[10px] font-manrope text-[16px] font-bold leading-[24px] h-[64px]"
                disabled={!isForgotValid || isForgotLoading}
                isLoading={isForgotLoading}
              >
                Get OTP
              </Button>

              <div className="flex items-center justify-center pt-2">
                <button
                  type="button"
                  onClick={() => setCurrentView("login")}
                  className="font-manrope text-[14px] text-secondary hover:text-primary font-medium transition-colors cursor-pointer"
                >
                  Back to Login
                </button>
              </div>
            </div>
          </form>
        )}

        {currentView === "reset" && (
          <form
            className="flex h-full w-full sm:w-[481px] py-8 flex-col px-4 sm:px-8 bg-white rounded-[12px]"
            onSubmit={handleSubmitReset(onResetSubmit)}
          >
            <div className="flex flex-col gap-2 items-center justify-center">
              <LogoIcon className="w-[50px] h-[50px]" />
              <p className="text-[30px] sm:text-[34px] font-medium font-manrope text-primary text-center">
                Reset Password
              </p>
              <p className="text-[14px] font-manrope text-secondary text-center max-w-[340px]">
                Enter the OTP code sent to your email and choose a new password.
              </p>
            </div>

            <div className="flex flex-col mt-6 gap-4">
              <Input
                label="Email"
                id="reset-email"
                value={resetEmail}
                disabled={true}
                className="w-full opacity-80"
                inputClassName="h-[50px] sm:!h-[60px] !bg-[#f5f5f5] cursor-not-allowed text-secondary"
              />

              <Input
                label="OTP Code"
                id="reset-otp"
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                className="w-full"
                inputClassName="h-[50px] sm:!h-[60px]"
                {...registerReset("otp")}
                error={resetErrors.otp && (resetErrors.otp.message as string)}
              />

              <Password
                label="New Password"
                id="reset-password"
                placeholder="Enter new password"
                autoComplete="new-password"
                className="w-full"
                inputClassName="h-[50px] sm:!h-[60px]"
                {...registerReset("password")}
                error={
                  resetErrors.password &&
                  (resetErrors.password.message as string)
                }
              />

              <Password
                label="Confirm Password"
                id="reset-confirm-password"
                placeholder="Confirm new password"
                autoComplete="new-password"
                className="w-full"
                inputClassName="h-[50px] sm:!h-[60px]"
                {...registerReset("confirm_password")}
                error={
                  resetErrors.confirm_password &&
                  (resetErrors.confirm_password.message as string)
                }
              />
            </div>

            <div className="flex flex-col gap-4 mt-8">
              <Button
                variant="validate"
                type="submit"
                className="rounded-[10px] font-manrope text-[16px] font-bold leading-[24px] h-[64px]"
                disabled={!isResetValid || isResetLoading}
                isLoading={isResetLoading}
              >
                Reset Password
              </Button>

              <div className="flex items-center justify-center pt-2">
                <button
                  type="button"
                  onClick={() => setCurrentView("login")}
                  className="font-manrope text-[14px] text-secondary hover:text-primary font-medium transition-colors cursor-pointer"
                >
                  Back to Login
                </button>
              </div>
          </div>
        </form>
        )}
      </div>
    </motion.div>
  );
};

export default SignInPage;













